import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplayActionsEnum,
  WorkspaceDisplayItemsEnum,
  WorkspaceDisplayObjectsEnum,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';

/*
 * Props.
 */

export interface WorkspacesAutoRecBannerProps {
  onShowMeClick: Handler;
  onDismissClick: Handler;
}

/*
 * Styles.
 */

const StyledBannerDiv = styled.div`
  display: grid;
  grid-template-areas:
    'left-icon title'
    ' . subtitle   '
    ' . buttons';
  grid-row-gap: 6px;
  row-gap: 6px;
  padding: 12px 12px 14px 12px;

  background-color: ${(p) => p.theme.p1BannerBackground};
  font-size: 13px;
  border-radius: 12px;
`;

const StyledIconDiv = styled.div`
  display: grid;
  grid-area: left-icon;
  padding-right: 6px;
`;

const StyledTitle = styled.div`
  display: grid;
  grid-area: title;

  font-weight: ${fontWeights.bold};
  color: ${(p) => p.theme.p1BannerTitle};
  line-height: 18px;

  margin-top: -3px;
`;

const StyledSubtitle = styled.div`
  display: grid;
  grid-area: subtitle;

  font-weight: ${fontWeights.medium};
  color: ${(p) => p.theme.p1BannerSubtitle};
  line-height: 17px;
  margin-bottom: 4px;
`;

const StyledButtonDiv = styled.div`
  display: grid;
  grid-area: buttons;
`;

/*
 * Component.
 */

export const WorkspacesAutoRecBanner: FC<WorkspacesAutoRecBannerProps> = (props) => {
  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_VIEW, {
      type: WorkspaceDisplayItemsEnum.BANNER,
      object: WorkspaceDisplayObjectsEnum.AUTO_RECOMMENDATIONS,
    });
  });

  const onDismiss = () => {
    props.onDismissClick();
    trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CLICK, {
      action: WorkspaceDisplayActionsEnum.DISMISS,
      section: WorkspaceDisplaySectionTrackingTypesEnum.P1_REC_BANNER,
    });
  };

  const onShowMe = () => {
    props.onShowMeClick();
    trackInterfaceEvent(InterfaceEventsEnum.P1_WORKSPACE_CLICK, {
      action: WorkspaceDisplayActionsEnum.DISPLAY,
      section: WorkspaceDisplaySectionTrackingTypesEnum.P1_REC_BANNER,
    });
  };

  const theme = useUserTheme();

  return (
    <StyledBannerDiv>
      <StyledIconDiv>
        <Icon name="lightbulbSmall" color={theme.p1BannerIcon} />
      </StyledIconDiv>
      <StyledTitle>
        <Trans id="workspaces-autorec-title">Stay focused by hiding what you rarely view</Trans>
      </StyledTitle>
      <StyledSubtitle>
        <Trans id="workspaces-autorec-subtitle">
          We found sidebar items that you haven&apos;t viewed in the last 4 weeks.
        </Trans>
      </StyledSubtitle>
      <StyledButtonDiv>
        <ButtonGroup spacingSize={VisualSizesEnum.SMALL}>
          <Button onClick={onShowMe} variant={theme.p1BannerButtonVariant} size={VisualSizesEnum.SMALL}>
            <Trans id="workspaces-autorec-show-me">Show me</Trans>
          </Button>
          <Button onClick={onDismiss} variant={theme.p1BannerLinkVariant} size={VisualSizesEnum.SMALL}>
            <Trans id="workspaces-autorec-dismiss">Dismiss</Trans>
          </Button>
        </ButtonGroup>
      </StyledButtonDiv>
    </StyledBannerDiv>
  );
};
